/**
 * Operator WebSocket client (downlink-only).
 *
 * Логика идентична виджету ([widget/src/ws.ts]) — только URL ведёт на
 * /ws/v1/operator, и токен — operator JWT. Дублирование пока сознательное:
 * пакеты разные, реальной shared-инфраструктуры под одно поведение пока
 * не требуется. Если в phase 8 добавим uplink/typing/presence —
 * рассмотрим вынос в `shared/ws-client/`.
 *
 * Reconnect: exponential backoff 1s → 2s → 4s → 8s → 16s → 30s. Сбрасывается
 * на successful open. close() отменяет любой запланированный reconnect.
 */

export type ConnectionState = 'connecting' | 'online' | 'offline';

export interface WebSocketLike {
  // Перегрузки для удобства внутри createOperatorWs. У моков допустимо
  // реализовать общую сигнатуру (type: string, fn: (ev: any) => void).
  addEventListener(type: 'open', fn: (ev: unknown) => void): void;
  addEventListener(type: 'message', fn: (ev: { data: string }) => void): void;
  addEventListener(
    type: 'close',
    fn: (ev: { code: number; reason: string }) => void,
  ): void;
  addEventListener(type: 'error', fn: (ev: unknown) => void): void;
  send(data: string): void;
  close(code?: number, reason?: string): void;
}

export interface WsEnvelope {
  type: string;
  payload?: unknown;
  id?: string;
}

export interface OperatorWsOptions {
  /** Базовый URL, без query. Пример: 'wss://api.anexica.com/ws/v1/operator'. */
  url: string;
  /** Operator JWT. */
  token: string;
  /** Conversation, на которую подписываемся. */
  conversationId: string;
  onMessage: (env: WsEnvelope) => void;
  onState?: (state: ConnectionState) => void;
  /** Тестовый хук — подменить конструктор WebSocket. */
  wsFactory?: (url: string) => WebSocketLike;
}

export interface OperatorWs {
  connect(): void;
  close(): void;
  /** Шлёт envelope, если соединение открыто. Иначе — silent no-op. */
  send(env: WsEnvelope): void;
}

const BACKOFF_DELAYS_MS = [1000, 2000, 4000, 8000, 16000, 30000];

export function createOperatorWs(opts: OperatorWsOptions): OperatorWs {
  const wsFactory =
    opts.wsFactory ?? ((url: string) => new WebSocket(url) as unknown as WebSocketLike);

  let current: WebSocketLike | null = null;
  let isOpenSock = false;
  let attempt = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let closedByUser = false;

  function setState(state: ConnectionState): void {
    opts.onState?.(state);
  }

  function cancelReconnect(): void {
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  }

  function scheduleReconnect(): void {
    const delay = BACKOFF_DELAYS_MS[Math.min(attempt, BACKOFF_DELAYS_MS.length - 1)];
    attempt += 1;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      doConnect();
    }, delay);
  }

  function doConnect(): void {
    setState('connecting');
    const url = buildUrl(opts.url, opts.token, opts.conversationId);
    const ws = wsFactory(url);
    current = ws;

    ws.addEventListener('open', () => {
      attempt = 0;
      isOpenSock = true;
      setState('online');
    });

    ws.addEventListener('message', (ev: { data: string }) => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(ev.data);
      } catch {
        return;
      }
      if (!isEnvelope(parsed)) return;
      opts.onMessage(parsed);
    });

    ws.addEventListener('close', () => {
      current = null;
      isOpenSock = false;
      if (closedByUser) {
        setState('offline');
        return;
      }
      setState('offline');
      scheduleReconnect();
    });

    ws.addEventListener('error', () => {
      // 'close' тоже сработает и запустит reconnect.
    });
  }

  return {
    connect(): void {
      closedByUser = false;
      cancelReconnect();
      doConnect();
    },
    close(): void {
      closedByUser = true;
      cancelReconnect();
      if (current) {
        current.close();
        current = null;
      }
      isOpenSock = false;
      setState('offline');
    },
    send(env: WsEnvelope): void {
      if (!current || !isOpenSock) return;
      try {
        current.send(JSON.stringify(env));
      } catch {
        // ignore — close-handler перезапустит
      }
    },
  };
}

function buildUrl(base: string, token: string, conversationId: string): string {
  const params = new URLSearchParams({ token, c: conversationId });
  return `${base}?${params.toString()}`;
}

function isEnvelope(v: unknown): v is WsEnvelope {
  return (
    typeof v === 'object' && v !== null && typeof (v as { type?: unknown }).type === 'string'
  );
}
